(function () {
  const CEP_STORAGE_KEY = 'elo_em_comum_shipping_cep';

  class ShippingCalculator extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-shipping-form]');
      this.cepInput = this.querySelector('[data-shipping-cep]');
      this.submitButton = this.querySelector('[data-shipping-submit]');
      this.loading = this.querySelector('[data-shipping-loading]');
      this.error = this.querySelector('[data-shipping-error]');
      this.results = this.querySelector('[data-shipping-results]');
      this.ratesList = this.querySelector('[data-shipping-rates]');
      this.i18n = this.#readI18n();
      this.abortController = null;

      this.form?.addEventListener('submit', this.#onSubmit);
      this.cepInput?.addEventListener('input', this.#onCepInput);
      this.#restoreCep();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.form?.removeEventListener('submit', this.#onSubmit);
      this.cepInput?.removeEventListener('input', this.#onCepInput);
    }

    #readI18n() {
      const node = this.querySelector('[data-shipping-i18n]');
      if (!node) return {};
      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        return {};
      }
    }

    #onCepInput = (event) => {
      if (!(event.target instanceof HTMLInputElement)) return;
      event.target.value = this.#formatCep(event.target.value);
    };

    #onSubmit = async (event) => {
      event.preventDefault();
      event.stopPropagation();

      const digits = this.#normalizeCep(this.cepInput?.value);
      if (digits.length !== 8) {
        this.#showError(this.i18n.invalid_cep);
        return;
      }

      this.abortController?.abort();
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.#setLoading(true);
      this.#hideError();
      this.#hideRates();

      try {
        const address = await this.#fetchAddress(digits, signal);
        if (!address) {
          this.#showError(this.i18n.cep_not_found);
          return;
        }

        await this.#ensureCartHasItems(signal);
        await this.#updateShippingAddress(address, signal);

        const rates = await this.#fetchShippingRates(address, signal);
        if (!rates.length) {
          this.#showError(this.i18n.no_rates);
          return;
        }

        sessionStorage.setItem(CEP_STORAGE_KEY, this.#formatCep(digits));
        this.#renderRates(rates, address);
        this.results?.classList.remove('hidden');
      } catch (requestError) {
        if (requestError instanceof DOMException && requestError.name === 'AbortError') return;

        if (requestError instanceof Error) {
          if (requestError.message === 'empty-cart') {
            this.#showError(this.i18n.empty_cart);
            return;
          }
          if (requestError.message === 'missing-variant') {
            this.#showError(this.i18n.missing_variant);
            return;
          }
        }

        this.#showError(this.i18n.error);
      } finally {
        this.#setLoading(false);
        this.abortController = null;
      }
    };

    #restoreCep() {
      if (!(this.cepInput instanceof HTMLInputElement)) return;
      const saved = sessionStorage.getItem(CEP_STORAGE_KEY);
      if (saved) this.cepInput.value = saved;
    }

    #setLoading(isLoading) {
      this.loading?.classList.toggle('hidden', !isLoading);
      if (this.submitButton instanceof HTMLButtonElement) {
        this.submitButton.disabled = isLoading;
      }
    }

    #showError(message) {
      if (!(this.error instanceof HTMLElement)) return;
      this.error.textContent = message || '';
      this.error.classList.remove('hidden');
    }

    #hideError() {
      this.error?.classList.add('hidden');
    }

    #hideRates() {
      this.results?.classList.add('hidden');
      if (this.ratesList instanceof HTMLElement) {
        this.ratesList.replaceChildren();
      }
    }

    #normalizeCep(value) {
      return String(value ?? '')
        .replace(/\D/g, '')
        .slice(0, 8);
    }

    #formatCep(digits) {
      const normalized = this.#normalizeCep(digits);
      if (normalized.length <= 5) return normalized;
      return `${normalized.slice(0, 5)}-${normalized.slice(5)}`;
    }

    async #fetchAddress(digits, signal) {
      const response = await fetch(`https://viacep.com.br/ws/${digits}/json/`, { signal });
      if (!response.ok) return null;

      const data = await response.json();
      if (!data || data.erro || !data.uf || !data.localidade) return null;

      return {
        ...data,
        cep: data.cep || digits,
      };
    }

    async #ensureCartHasItems(signal) {
      const cartResponse = await fetch('/cart.js', { signal });
      const cart = await cartResponse.json();
      if (cart.item_count > 0) return;

      if (this.dataset.context !== 'pdp') {
        throw new Error('empty-cart');
      }

      const variantId = this.#getSelectedVariantId();
      const quantity = this.#getSelectedQuantity();
      if (!variantId) throw new Error('missing-variant');

      const addUrl = window.routes?.cart_add_url || '/cart/add';
      const addResponse = await fetch(addUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity }],
        }),
        signal,
      });

      if (!addResponse.ok) throw new Error('add-failed');
    }

    #getSelectedVariantId() {
      const scope = this.closest('product-info') || document;
      const variantInput = scope.querySelector(
        'product-form form[action*="/cart/add"] input[name="id"], form[action*="/cart/add"] input[name="id"]'
      );
      if (variantInput instanceof HTMLInputElement && variantInput.value) {
        return variantInput.value;
      }
      return this.dataset.variantId || null;
    }

    #getSelectedQuantity() {
      const scope = this.closest('product-info') || document;
      const quantityInput = scope.querySelector('input[name="quantity"]');
      if (quantityInput instanceof HTMLInputElement) {
        const quantity = Number.parseInt(quantityInput.value, 10);
        if (!Number.isNaN(quantity) && quantity > 0) return quantity;
      }
      return 1;
    }

    async #updateShippingAddress(address, signal) {
      const shippingAddress = {
        zip: this.#formatCep(address.cep || ''),
        country: 'Brazil',
        province: address.uf,
        city: address.localidade,
      };

      const updateUrl = window.routes?.cart_update_url || '/cart/update.js';
      const response = await fetch(updateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ shipping_address: shippingAddress }),
        signal,
      });

      if (!response.ok) throw new Error('address-update-failed');
    }

    async #fetchShippingRates(address, signal) {
      const params = new URLSearchParams({
        'shipping_address[zip]': this.#formatCep(address.cep || ''),
        'shipping_address[country]': 'Brazil',
        'shipping_address[province]': address.uf,
        'shipping_address[city]': address.localidade,
      });

      const directResponse = await fetch(`/cart/shipping_rates.json?${params.toString()}`, { signal });
      if (directResponse.ok) {
        const directData = await directResponse.json();
        if (directData?.shipping_rates?.length) return directData.shipping_rates;
      }

      const prepareUrl = '/cart/prepare_shipping_rates.json';
      await fetch(prepareUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          shipping_address: {
            zip: this.#formatCep(address.cep || ''),
            country: 'Brazil',
            province: address.uf,
            city: address.localidade,
          },
        }),
        signal,
      });

      for (let attempt = 0; attempt < 8; attempt += 1) {
        await new Promise((resolve) => window.setTimeout(resolve, 400));
        const asyncResponse = await fetch('/cart/async_shipping_rates.json', { signal });
        if (!asyncResponse.ok) continue;
        const asyncData = await asyncResponse.json();
        if (asyncData?.shipping_rates?.length) return asyncData.shipping_rates;
      }

      return [];
    }

    #formatMoney(priceValue) {
      const amount = Number(priceValue);
      if (!Number.isFinite(amount)) return String(priceValue ?? '');
      if (amount === 0) return this.i18n.free || 'Grátis';

      const moneyFormat = this.dataset.moneyFormat || 'R$ {{amount_with_comma_separator}}';
      const withComma = amount.toFixed(2).replace('.', ',');
      const withDot = amount.toFixed(2);
      const noDecimals = String(Math.round(amount));

      if (moneyFormat.includes('amount_with_comma_separator')) {
        return moneyFormat.replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, withComma);
      }
      if (moneyFormat.includes('amount_no_decimals')) {
        return moneyFormat.replace(/\{\{\s*amount_no_decimals\s*\}\}/, noDecimals);
      }
      return moneyFormat.replace(/\{\{\s*amount\s*\}\}/, withDot);
    }

    #renderRates(rates, address) {
      if (!(this.ratesList instanceof HTMLElement)) return;

      const locationLabel = String(this.i18n.rates_for || '')
        .replace('[city]', address.localidade)
        .replace('[uf]', address.uf);

      this.ratesList.replaceChildren();

      const heading = document.createElement('p');
      heading.className = 'shipping-calculator__rates-heading';
      heading.textContent = locationLabel;
      this.ratesList.append(heading);

      const list = document.createElement('ul');
      list.className = 'shipping-calculator__rates';

      rates.forEach((rate) => {
        const item = document.createElement('li');
        item.className = 'shipping-calculator__rate';

        const name = document.createElement('span');
        name.className = 'shipping-calculator__rate-name';
        name.textContent = rate.presentment_name || rate.name;

        const price = document.createElement('span');
        price.className = 'shipping-calculator__rate-price';
        price.textContent = this.#formatMoney(rate.price);

        item.append(name, price);
        list.append(item);
      });

      this.ratesList.append(list);

      if (this.dataset.context === 'pdp') {
        const note = document.createElement('p');
        note.className = 'shipping-calculator__note';
        note.textContent = this.i18n.pdp_note || '';
        this.ratesList.append(note);
      }
    }
  }

  if (!customElements.get('shipping-calculator')) {
    customElements.define('shipping-calculator', ShippingCalculator);
  }
})();
